import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { restoreSession, installAuthKeeperListeners } from "../pwa/authKeeper";
import { setSentryUser, clearSentryUser } from "../pwa/sentry";
import { resetarAvisoSessaoExpirada } from "../api";
import * as sessao from "../platform/storage";
import * as pushNativo from "../platform/push";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenant, setTenant] = useState(null);
  const navigate = useNavigate();

  // Sem `useCallback` com dependencia do tenant de proposito: esta funcao nao
  // le estado, so escreve. Falha em silencio — perder o refresh dos modulos e
  // menos grave que travar o boot por causa de rede ruim.
  const atualizarTenantDoServidor = useCallback(async () => {
    try {
      const api = (await import('../api')).default;
      const res = await api.get('/tenant/info');
      if (res?.data) {
        const salvo = sessao.getItem('tenant');
        const anterior = salvo ? JSON.parse(salvo) : {};
        const atualizado = { ...anterior, ...res.data };
        sessao.setItem('tenant', JSON.stringify(atualizado));
        setTenant(atualizado);
      }
    } catch { /* offline, token vencido, servidor fora: segue com o que havia */ }
  }, []);

  const applyAuthState = useCallback((hasToken) => {
    if (hasToken) {
      setIsAuthenticated(true);
      setIsAdmin(sessao.getItem("is_admin") === "true");
      setIsSuperAdmin(sessao.getItem("is_superadmin") === "true");
      const savedTenant = sessao.getItem("tenant");
      const parsed = savedTenant ? JSON.parse(savedTenant) : null;
      setTenant(parsed);
      // Identifica usuario no Sentry — email vem do token mas guardamos
      // separado para evitar parse do JWT aqui.
      const email = sessao.getItem("user_email");
      setSentryUser(email, parsed?.slug);
      // Token do FCM rotaciona sozinho (reinstalacao, restore de backup), entao
      // registramos a cada boot autenticado — nao so uma vez. No-op na web.
      if (pushNativo.disponivel()) pushNativo.registrar();
      // O tenant guardado veio do login e pode ter meses. Modulo ligado no
      // suporte so apareceria depois de sair e entrar de novo — e "liguei e
      // nao apareceu" e chamado de suporte garantido. /tenant/info e barato
      // (le o tenant que o middleware ja resolveu) e nao bloqueia o boot.
      atualizarTenantDoServidor();
    } else {
      setIsAuthenticated(false);
      clearSentryUser();
    }
  }, [atualizarTenantDoServidor]);

  const checkAuth = useCallback(async () => {
    // No app nativo o token vive no storage do sistema, que e assincrono.
    // Precisa estar em memoria ANTES de decidir autenticado/nao, senao o boot
    // le vazio e joga a usuaria pro login mesmo com sessao valida.
    // Na web isso e no-op e resolve no mesmo tick.
    await sessao.hydrate();

    // "Restore na home": antes de decidir autenticado/nao, tenta refrescar
    // se o access esta vencido mas o refresh existe (caso classico iOS PWA
    // voltando depois de 12h+).
    const access = sessao.getItem("token");
    const refresh = sessao.getItem("refresh_token");

    if (!access && !refresh) {
      applyAuthState(false);
      return;
    }

    if (!access && refresh) {
      const r = await restoreSession({ force: true, reason: "boot-no-access" });
      applyAuthState(r.ok);
      return;
    }

    // Tem access — assume autenticado e dispara refresh em background se perto
    // de expirar (nao bloqueia render).
    applyAuthState(true);
    restoreSession({ reason: "boot" }).catch(() => {});
  }, [applyAuthState]);

  const login = useCallback((token, admin, refreshToken, tenantData, superAdmin) => {
    sessao.setItem("token", token);
    sessao.setItem("is_admin", admin ? "true" : "false");
    sessao.setItem("is_superadmin", superAdmin ? "true" : "false");
    if (refreshToken) sessao.setItem("refresh_token", refreshToken);
    if (tenantData) sessao.setItem("tenant", JSON.stringify(tenantData));
    // Decodifica email do JWT para o Sentry identificar o usuario.
    try {
      const part = token.split('.')[1];
      const padded = part.padEnd(part.length + (4 - (part.length % 4)) % 4, '=');
      const claims = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
      if (claims.sub) sessao.setItem("user_email", claims.sub);
      setSentryUser(claims.sub, tenantData?.slug);
    } catch { /* ignore */ }
    setIsAuthenticated(true);
    setIsAdmin(admin);
    setIsSuperAdmin(superAdmin || false);
    setTenant(tenantData || null);
    // Reset do gate do toast "sessao expirou" — sem isso, apos um ciclo
    // (login -> expira -> re-login -> expira de novo) o segundo aviso nao
    // aparece porque a flag ficou true no modulo.
    resetarAvisoSessaoExpirada();
    navigate("/", { replace: true });
  }, [navigate]);

  const refreshTenant = useCallback((tenantData) => {
    if (tenantData) {
      sessao.setItem("tenant", JSON.stringify(tenantData));
      setTenant(tenantData);
    }
  }, []);

  const logout = useCallback(async () => {
    // Tira o device do backend antes de perder o token de auth — depois de
    // limpar a sessao a chamada iria sem Authorization.
    if (pushNativo.disponivel()) await pushNativo.desregistrar();

    // Revoga o token no backend ANTES de limpar — sem isso o refresh token
    // sobrevive ate 30d na conta do atacante se o storage foi comprometido.
    // Lazy require pra evitar ciclo de import.
    try {
      const api = (await import('../api')).default;
      // Tenta com refresh token primeiro (mais valioso); se nao tiver, manda o access.
      const refreshToken = sessao.getItem('refresh_token');
      const accessToken = sessao.getItem('token');
      const token = refreshToken || accessToken;
      if (token) {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
          _retry: true, // evita o interceptor tentar refresh em 401
        }).catch(() => {});
      }
    } catch { /* ignora — logout client-side acontece independente */ }

    sessao.removeItem("token");
    sessao.removeItem("is_admin");
    sessao.removeItem("is_superadmin");
    sessao.removeItem("refresh_token");
    sessao.removeItem("tenant");
    sessao.removeItem("user_email");
    localStorage.removeItem("onboarding_completo");
    sessionStorage.removeItem("primeiros_passos_fab_dismissed");
    clearSentryUser();
    setIsAuthenticated(false);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setTenant(null);
    // Reset gate do toast tambem no logout — usuario pode logar de novo por
    // outro caminho (reload em /login) sem passar por AuthContext.login.
    resetarAvisoSessaoExpirada();
    navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    installAuthKeeperListeners();
    checkAuth();
  }, [checkAuth]);

  if (isAuthenticated === null) {
    return <div>Carregando...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isAdmin, isSuperAdmin, tenant, login, logout, refreshTenant }}>
      {children}
    </AuthContext.Provider>
  );
};
